import React, { useState, useEffect } from 'react';
import { SelectBox, Button } from 'devextreme-react';
import { HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import axios from 'axios';
import { getBaseApiUrl } from '../../../config/applicationConfig';
import { useTranslation } from 'react-i18next';

const LiveMonitoringPage = () => {
    const { t } = useTranslation();
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [monitoringLogs, setMonitoringLogs] = useState([]);
    const [isMonitoring, setIsMonitoring] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connection, setConnection] = useState(null);

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const baseUrl = getBaseApiUrl();
                const response = await axios.get(`${baseUrl}/api/SbomProductCommunication/GetViewList`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                });
                const data = response.data;
                console.log("GetViewList Raw Response:", data);
                
                let deviceList = Array.isArray(data) ? data : (data.data || []);
                
                // Normalize data to handle PascalCase vs camelCase
                deviceList = deviceList.map(item => {
                    const ip = item.WanIpAddress || item.wanIpAddress || item.IpAddress || item.ipAddress || "";
                    const central = item.CentralName || item.centralName || "";
                    return {
                        ...item,
                        // Create normalized fields for display/value
                        _display: central ? `${ip} - ${central}` : ip,
                        _value: ip
                    };
                });
                
                console.log("Normalized Device List:", deviceList);
                setDevices(deviceList);
            } catch (error) {
                console.error("Error fetching devices:", error);
            }
        };
        fetchDevices();

        return () => {
            if (connection) {
                try {
                    connection.stop();
                } catch (e) {
                    console.error("Cleanup stop error:", e);
                }
            }
        };
    }, []);

    const handleStartMonitoring = async () => {
        if (!selectedDevice) return;

        if (isMonitoring) {
             if (connection) {
                try {
                    await connection.stop();
                } catch (e) {
                    console.error("Stop error:", e);
                }
                setConnection(null);
            }
            setIsMonitoring(false);
            setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] İzleme durduruldu.`, ...prev]);
            return;
        }

        setIsConnecting(true);
        setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] Bağlantı başlatılıyor...`, ...prev]);

        const baseUrl = getBaseApiUrl();
        const hubUrl = `${baseUrl}/liveMonitoringHub`;

        const newConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => localStorage.getItem("token"),
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .build();

        newConnection.on("ReceiveLiveData", (data) => {
            console.log("ReceiveLiveData triggered:", data);
            const timestamp = new Date().toLocaleTimeString();
            let content = "";
            try {
                content = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
            } catch (e) {
                content = "Data parse error";
            }
            const logEntry = `[${timestamp}] ${content}`;
            setMonitoringLogs(prevLogs => [logEntry, ...prevLogs]);
        });

        newConnection.onclose((e) => {
            console.log("Connection closed", e);
            setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] Bağlantı kapandı: ${e ? e.message : 'Normal kapanış'}`, ...prev]);
            setIsMonitoring(false);
            setConnection(null);
        });

        newConnection.onreconnecting((e) => {
            setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] Yeniden bağlanılıyor...`, ...prev]);
        });

        newConnection.onreconnected((connectionId) => {
            setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] Yeniden bağlandı. ID: ${connectionId}`, ...prev]);
        });

        try {
            await newConnection.start();
            console.log("Connected to SignalR");
            setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] Api dinleme servisine bağlandı.`, ...prev]);
            
            await newConnection.invoke("StartMonitoring", selectedDevice);
            setMonitoringLogs(prev => [
                `[${new Date().toLocaleTimeString()}] Bilgiler bekleniyor...`,
                `[${new Date().toLocaleTimeString()}] İzleme isteği gönderildi: ${selectedDevice}`,
                ...prev
            ]);
            
            setConnection(newConnection);
            setIsMonitoring(true);
        } catch (err) {
            console.error("Connection failed: ", err);
            setMonitoringLogs(prev => [`[${new Date().toLocaleTimeString()}] HATA: Bağlantı başarısız. ${err.message}`, ...prev]);
        } finally {
            setIsConnecting(false);
        }
    };

    return (
        <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
                <h5 className="card-title">{t('liveMonitoring')}</h5>
            </div>
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-md-3">
                        <SelectBox
                                dataSource={devices}
                                displayExpr="_display"
                                valueExpr="_value"
                                placeholder={t('selectDevice')}
                                searchEnabled={true}
                                searchExpr={['_display', '_value', 'WanIpAddress', 'wanIpAddress', 'CentralName', 'centralName']}
                                onValueChanged={(e) => setSelectedDevice(e.value)}
                                showClearButton={true}
                            />
                    </div>
                    <div className="col-md-2">
                        <Button
                            text={isConnecting ? "Bağlanıyor..." : (isMonitoring ? t('stopMonitoring') : t('startMonitoring'))}
                            type={isMonitoring ? "danger" : "success"}
                            onClick={handleStartMonitoring}
                            disabled={!selectedDevice || isConnecting}
                        />
                    </div>
                </div>
                
                <div className="row">
                    <div className="col-12">
                        <label className="form-label">{t('monitoringLogs') || 'Canlı Veri Akışı'}</label>
                        <div 
                            style={{
                                backgroundColor: '#1e1e1e', 
                                color: '#00ff00', 
                                fontFamily: 'Consolas, monospace',
                                padding: '15px', 
                                borderRadius: '5px',
                                height: '500px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                fontSize: '12px',
                                border: '1px solid #333'
                            }}
                        >
                            {monitoringLogs.length === 0 ? (
                                <span style={{color: '#666'}}>Veri bekleniyor...</span>
                            ) : (
                                monitoringLogs.map((log, index) => (
                                    <div key={index} style={{borderBottom: '1px solid #333', paddingBottom: '5px', marginBottom: '5px'}}>
                                        {log}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveMonitoringPage;
